import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DraftsRoundedIcon from "@mui/icons-material/DraftsRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";

import { useAuth } from "../../../hooks/useAuth";
import GlassCard from "./components/GlassCard";
import StepperRail from "./components/StepperRail";
import StepHeader from "./components/StepHeader";
import StepFooter from "./components/StepFooter";
import RightPreview from "./components/RightPreview";
import { useNovoEvento } from "./useNovoEvento";
import { validateStep, hasErrors } from "./utils/validators";

import IdentidadeStep from "./components/steps/IdentidadeStep";
import DataLocalStep from "./components/steps/DataLocalStep";
import DescricaoStep from "./components/steps/DescricaoStep";
import IngressosStep from "./components/steps/IngressosStep";
import VendasStep from "./components/steps/VendasStep";
import RegrasStep from "./components/steps/RegrasStep";
import RevisaoStep from "./components/steps/RevisaoStep";

import {
  createEventRtdb,
  updateEventRtdb,
} from "../../../services/events/eventsService";

const STEPS = [
  {
    key: "identidade",
    label: "Identidade",
    short: "Nome, categoria e banner",
    helper:
      "Defina o título, categoria, classificação e a identidade visual do evento.",
  },
  {
    key: "dataLocal",
    label: "Data & Local",
    short: "Quando e onde",
    helper:
      "Informe data, horário e endereço com clareza para evitar dúvidas do público.",
  },
  {
    key: "descricao",
    label: "Descrição",
    short: "Storytelling do evento",
    helper:
      "Monte uma página mais vendável com descrição, line-up e observações importantes.",
  },
  {
    key: "ingressos",
    label: "Ingressos",
    short: "Lotes e setores",
    helper: "Cadastre os ingressos com preço, quantidade e janela de venda.",
  },
  {
    key: "vendas",
    label: "Vendas & Comissários",
    short: "Operação comercial",
    helper: "Configure limites, taxa, pontos físicos e comissários oficiais.",
  },
  {
    key: "regras",
    label: "Regras",
    short: "Políticas do evento",
    helper: "Defina acesso de menores, reembolso e os avisos gerais da página.",
  },
  {
    key: "revisao",
    label: "Revisão",
    short: "Checklist final",
    helper: "Confira o resumo completo antes de salvar ou publicar o evento.",
  },
];

export default function NovoEventoPage() {
  const { state, actions } = useNovoEvento();
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eventId, setEventId] = useState(null);
  const [savedInfo, setSavedInfo] = useState({ mode: "save", eventId: null });

  const { user } = useAuth();
  const organizerId = user?.uid || "";

  const CurrentStep = useMemo(() => {
    if (step === 0) return IdentidadeStep;
    if (step === 1) return DataLocalStep;
    if (step === 2) return DescricaoStep;
    if (step === 3) return IngressosStep;
    if (step === 4) return VendasStep;
    if (step === 5) return RegrasStep;
    return RevisaoStep;
  }, [step]);

  const currentMeta = STEPS[step] || STEPS[0];
  const progress = ((step + 1) / STEPS.length) * 100;

  function goNext() {
    const e = validateStep(step, state);
    setErrors(e);
    if (hasErrors(e)) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    setSubmitError("");
  }

  function goBack() {
    setErrors({});
    setSubmitError("");
    setStep((s) => Math.max(0, s - 1));
  }

  async function handleSaveDraft() {
    try {
      setSaving(true);
      setSubmitError("");

      let id = eventId;
      const saveOptions = {
        status: "draft",
        organizerId,
        createdByUid: user?.uid || "",
      };

      if (!id) {
        id = await createEventRtdb(state, saveOptions);
        setEventId(id);
      } else {
        await updateEventRtdb(id, state, saveOptions);
      }

      setSavedInfo({ mode: "draft", eventId: id });
      setSavedOk(true);
    } catch (err) {
      console.error(err);
      setSubmitError("Não consegui salvar o rascunho. Veja o console.");
    } finally {
      setSaving(false);
    }
  }

  async function onSubmit() {
    const e = validateStep(step, state);
    setErrors(e);
    if (hasErrors(e)) return;

    try {
      setSaving(true);
      setSubmitError("");

      let id = eventId;
      const saveOptions = {
        status: "online",
        organizerId,
        createdByUid: user?.uid || "",
      };

      if (!id) {
        id = await createEventRtdb(state, saveOptions);
        setEventId(id);
      } else {
        await updateEventRtdb(id, state, saveOptions);
      }

      setSavedInfo({ mode: "save", eventId: id });
      setSavedOk(true);
    } catch (err) {
      console.error(err);
      setSubmitError("Não consegui salvar o evento. Veja o console.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={2.5}>
          <GlassCard
            sx={{
              p: { xs: 2, md: 3 },
              overflow: "hidden",
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", lg: "center" }}
                gap={2}
              >
                <Stack spacing={1.2} sx={{ maxWidth: 760 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Chip
                      icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />}
                      label="Fluxo premium de criação"
                      sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(0,225,255,0.12)",
                        color: "#8ef4ff",
                        border: "1px solid rgba(0,225,255,0.22)",
                      }}
                    />
                    <Chip
                      label={`Etapa ${step + 1} de ${STEPS.length}`}
                      sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(255,255,255,0.06)",
                      }}
                    />
                  </Stack>

                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 950,
                        letterSpacing: -1,
                        lineHeight: 1.05,
                        mb: 0.8,
                      }}
                    >
                      Criar evento com uma experiência mais clara, bonita e
                      intuitiva.
                    </Typography>
                    <Typography
                      sx={{ color: "rgba(255,255,255,0.72)", maxWidth: 760 }}
                    >
                      O objetivo aqui é preencher rápido, sem se perder. Cada
                      etapa foi pensada para o organizador montar uma página
                      forte, organizada e pronta para vender bem.
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.2}
                  sx={{ width: { xs: "100%", lg: "auto" } }}
                >
                  <Button
                    fullWidth={false}
                    variant="outlined"
                    startIcon={<DeleteSweepRoundedIcon />}
                    disabled={saving}
                    onClick={() => setOpenResetDialog(true)}
                    sx={{
                      minHeight: 46,
                      px: 2,
                      borderRadius: 2,
                      fontWeight: 900,
                      borderColor: "rgba(255,255,255,0.14)",
                    }}
                  >
                    Limpar
                  </Button>
                  <Button
                    fullWidth={false}
                    variant="contained"
                    startIcon={<DraftsRoundedIcon />}
                    disabled={saving}
                    onClick={handleSaveDraft}
                    sx={{
                      minHeight: 46,
                      px: 2.2,
                      borderRadius: 2,
                      fontWeight: 900,
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.08))",
                      color: "#fff",
                      boxShadow: "none",
                      border: "1px solid rgba(255,255,255,0.12)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.1))",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {saving ? "Salvando..." : "Salvar rascunho"}
                  </Button>
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 13,
                      color: "rgba(255,255,255,0.68)",
                    }}
                  >
                    Progresso do fluxo
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: 13,
                      color: "rgba(255,255,255,0.9)",
                    }}
                  >
                    {Math.round(progress)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 10,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.08)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 2,
                      background:
                        "linear-gradient(90deg, rgba(0,225,255,0.95), rgba(109,255,178,0.95))",
                    },
                  }}
                />
                <Typography
                  sx={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}
                >
                  Agora você está em <strong>{currentMeta.label}</strong>:{" "}
                  {currentMeta.short}.
                </Typography>
              </Stack>
            </Stack>
          </GlassCard>

          {submitError ? (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: "rgba(255,107,107,0.4)",
                bgcolor: "rgba(255,107,107,0.08)",
              }}
            >
              {submitError}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1.15fr) 420px" },
              gap: 2.5,
              alignItems: "start",
            }}
          >
            <GlassCard sx={{ p: { xs: 1.5, md: 2 } }}>
              <Stack spacing={2}>
                <StepHeader
                  steps={STEPS}
                  step={step}
                  title={currentMeta.label}
                  helper={currentMeta.helper}
                />

                <StepperRail
                  steps={STEPS}
                  step={step}
                  onStepClick={(i) => setStep(i)}
                />

                <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                <Box sx={{ minHeight: { md: 520 } }}>
                  <CurrentStep
                    state={state}
                    actions={actions}
                    errors={errors}
                  />
                </Box>

                <StepFooter
                  step={step}
                  lastStep={STEPS.length - 1}
                  onBack={goBack}
                  onNext={goNext}
                  onSubmit={onSubmit}
                  nextLabel={STEPS[Math.min(step + 1, STEPS.length - 1)]?.label}
                  saving={saving}
                />
              </Stack>
            </GlassCard>

            <RightPreview state={state} />
          </Box>
        </Stack>
      </Container>

      <Dialog
        open={openResetDialog}
        onClose={() => setOpenResetDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(7,12,24,0.92)",
            backdropFilter: "blur(20px)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 950, pb: 1 }}>
          Limpar formulário?
        </DialogTitle>
        <DialogContent sx={{ color: "rgba(255,255,255,0.74)" }}>
          Isso vai apagar todos os campos preenchidos deste evento no formulário
          atual.
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.4 }}>
          <Button
            variant="outlined"
            disabled={saving}
            sx={{ borderRadius: 2, fontWeight: 900 }}
            onClick={() => setOpenResetDialog(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              background: "linear-gradient(135deg, #ff5f7a, #ff8fa3)",
              color: "#18070b",
            }}
            onClick={() => {
              actions.reset();
              setErrors({});
              setSubmitError("");
              setEventId(null);
              setOpenResetDialog(false);
            }}
          >
            Sim, limpar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={savedOk}
        onClose={() => setSavedOk(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(7,12,24,0.94)",
            backdropFilter: "blur(20px)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 950,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleRoundedIcon sx={{ color: "#6dffb2" }} />
          {savedInfo.mode === "draft" ? "Rascunho salvo" : "Evento publicado!"}
        </DialogTitle>
        <DialogContent sx={{ color: "rgba(255,255,255,0.74)" }}>
          {savedInfo.mode === "draft"
            ? "Seu rascunho foi salvo com sucesso. Você pode continuar editando e publicar quando quiser."
            : "Seu evento foi publicado com sucesso e já pode aparecer na vitrine para os clientes."}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.4 }}>
          <Button
            variant="contained"
            startIcon={<EventAvailableRoundedIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              background:
                "linear-gradient(135deg, rgba(0,225,255,0.95), rgba(109,255,178,0.95))",
              color: "#052018",
            }}
            onClick={() => setSavedOk(false)}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
